import { Apartment, ApartmentStats, GroupStats, TotalStats } from "@/models/responses";
import { useMemo } from "react";
import { useAppData } from "./useAppData";
import { useGetApartmentStatsQuery } from "@/store/booking-management-api";

export interface StatFilter {
    (apartment: Apartment): boolean;
}

interface TotalStatsWithApartments extends TotalStats {
    numberOfApartments: number;
}

export function useAggregatedStats(
    startDate: Date,
    endDate: Date,
    filters: StatFilter[] = [],
): {
    groupStats: GroupStats[];
    totalStats: TotalStatsWithApartments;
    apartmentStats: ApartmentStats[];
    isLoading: boolean;
    error: unknown;
} {
    const { data: apartmentStats, isLoading, error } = useGetApartmentStatsQuery({ startDate, endDate });
    const { apartmentDetailsMap } = useAppData();

    const { filteredApartmentStats, groupStats, totalStats } = useMemo(() => {
        if (!apartmentStats) {
            return { groupStats: [], totalStats: createEmptyTotalStats() };
        }

        // Group apartments by groupId
        const statsByGroup = new Map<number, ApartmentStats[]>();

        const filteredApartmentStats = apartmentStats.filter((stat) => {
            const apartment = apartmentDetailsMap.get(stat.apartmentId);
            if (!apartment) {
                return false;
            }
            return filters.every((filter) => filter(apartment));
        });
        for (const stat of filteredApartmentStats) {
            const apartment = apartmentDetailsMap.get(stat.apartmentId);
            if (!apartment) {
                continue;
            }

            const groupStats = statsByGroup.get(apartment.groupId) || [];
            groupStats.push(stat);
            statsByGroup.set(apartment.groupId, groupStats);
        }
        // Calculate group stats
        const groupStats: GroupStats[] = [];
        const totalStats: TotalStatsWithApartments = createEmptyTotalStats();

        totalStats.numberOfApartments = 0;
        for (const [groupId, stats] of statsByGroup.entries()) {
            const numberOfApartments = stats.length;
            const groupStat: GroupStats = {
                ...createEmptyGroupStat(groupId),
                numberOfApartments,
            };

            totalStats.numberOfApartments += stats.length;
            // Sum non-average stats
            for (const stat of stats) {
                groupStat.daysBooked += stat.daysBooked;
                groupStat.daysBookedForStartingInPeriod += stat.daysBookedForStartingInPeriod;
                groupStat.grossIncome += stat.grossIncome;
                groupStat.grossIncomeAdjusted += stat.grossIncomeAdjusted;
                groupStat.netIncome += stat.netIncome;
                groupStat.netIncomeAdjusted += stat.netIncomeAdjusted;
                groupStat.numberOfReservationsReceived += stat.numberOfReservationsReceived;
                groupStat.numberOfVisits += stat.numberOfVisits;
                groupStat.leadDays = (groupStat.leadDays || 0) + (stat.leadDays || 0);
                groupStat.platformCommissions += stat.platformCommissions;
                groupStat.cleaningFees += stat.cleaningFees;
                groupStat.sustainabilityFee += stat.sustainabilityFee;
                groupStat.temporaryStayDuty += stat.temporaryStayDuty;
                groupStat.vat += stat.vat;
                groupStat.availableBookingDays += stat.availableBookingDays;
                groupStat.daysBlocked += stat.daysBlocked;
                // console.log(groupStat);

                // Also sum for total stats
                totalStats.daysBooked += stat.daysBooked;
                totalStats.daysBookedForStartingInPeriod += stat.daysBookedForStartingInPeriod;
                totalStats.grossIncome += stat.grossIncome;
                totalStats.grossIncomeAdjusted += stat.grossIncomeAdjusted;
                totalStats.netIncome += stat.netIncome;
                totalStats.netIncomeAdjusted += stat.netIncomeAdjusted;
                totalStats.numberOfReservationsReceived += stat.numberOfReservationsReceived;
                totalStats.numberOfVisits += stat.numberOfVisits;
                totalStats.leadDays = (totalStats.leadDays || 0) + (stat.leadDays || 0);
                totalStats.platformCommissions += stat.platformCommissions;
                totalStats.cleaningFees += stat.cleaningFees;
                totalStats.sustainabilityFee += stat.sustainabilityFee;
                totalStats.temporaryStayDuty += stat.temporaryStayDuty;
                totalStats.vat += stat.vat;
                totalStats.availableBookingDays += stat.availableBookingDays;
                totalStats.daysBlocked += stat.daysBlocked;
            }

            // Calculate group averages
            if (groupStat.numberOfReservationsReceived > 0) {
                groupStat.averageLengthOfStay = groupStat.daysBookedForStartingInPeriod / groupStat.numberOfReservationsReceived;
                groupStat.averageBlt = (groupStat.leadDays || 0) / groupStat.numberOfReservationsReceived;
            }

            if (groupStat.daysBooked > 0) {
                groupStat.grossAdr = groupStat.grossIncomeAdjusted / groupStat.daysBooked;
                groupStat.netAdr = groupStat.netIncomeAdjusted / groupStat.daysBooked;
            }

            if (groupStat.availableBookingDays > groupStat.daysBlocked) {
                groupStat.occupancy = (100 * groupStat.daysBooked) / (groupStat.availableBookingDays - groupStat.daysBlocked);
            }

            groupStats.push(groupStat);
        }

        // Calculate total averages
        if (totalStats.numberOfReservationsReceived > 0) {
            totalStats.averageLengthOfStay = totalStats.daysBookedForStartingInPeriod / totalStats.numberOfReservationsReceived;
            totalStats.averageBlt = (totalStats.leadDays || 0) / totalStats.numberOfReservationsReceived;
        }

        if (totalStats.daysBooked > 0) {
            totalStats.grossAdr = totalStats.grossIncomeAdjusted / totalStats.daysBooked;
            totalStats.netAdr = totalStats.netIncomeAdjusted / totalStats.daysBooked;
        }

        if (totalStats.availableBookingDays > totalStats.daysBlocked) {
            totalStats.occupancy = (100 * totalStats.daysBooked) / (totalStats.availableBookingDays - totalStats.daysBlocked);
        }

        return { filteredApartmentStats, groupStats, totalStats, isLoading, error };
    }, [filters, apartmentStats, isLoading, error, apartmentDetailsMap]);

    return {
        apartmentStats: filteredApartmentStats || [],
        groupStats,
        totalStats,
        isLoading,
        error,
    };
}

export function createEmptyGroupStat(id: number): GroupStats {
    return {
        groupId: id,
        ...createEmptyTotalStats(),
    };
}

function createEmptyTotalStats(): TotalStatsWithApartments {
    return {
        numberOfApartments: 0,
        availableBookingDays: 0,
        averageBlt: 0,
        averageLengthOfStay: 0,
        cleaningFees: 0,
        daysBlocked: 0,
        daysBooked: 0,
        daysBookedForStartingInPeriod: 0,
        grossAdr: 0,
        grossIncome: 0,
        grossIncomeAdjusted: 0,
        leadDays: 0,
        netAdr: 0,
        netIncome: 0,
        netIncomeAdjusted: 0,
        numberOfReservationsReceived: 0,
        numberOfVisits: 0,
        occupancy: 0,
        platformCommissions: 0,
        sustainabilityFee: 0,
        temporaryStayDuty: 0,
        businessFee: 0,
        vat: 0,
    };
}
